import { useState, useEffect } from 'react';
import { DEAL_COMMENTS_NR_ACTIONS } from './dealCommentsConstants';

const SCRIPT_URL = 'https://static.comments.coxautoservices.com/contextual-comments.esm.js';
const ELEMENT_NAME = 'enterprise-product-experience-contextual-comments';

let scriptLoadPromise: Promise<void> | null = null;

function loadContextualCommentsScript(): Promise<void> {
    if (!scriptLoadPromise) {
        scriptLoadPromise = new Promise<void>((resolve, reject) => {
            if (customElements.get(ELEMENT_NAME)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.type = 'module';
            script.src = SCRIPT_URL;
            script.addEventListener('load', () => {
                customElements.whenDefined(ELEMENT_NAME).then(() => resolve());
            });
            script.addEventListener('error', () => {
                reject(new Error('Failed to load contextual-comments script'));
            });
            document.head.appendChild(script);
        });
    }
    return scriptLoadPromise;
}

/** Resets the singleton promise — only for use in tests. */
export function resetContextualCommentsLoader(): void {
    scriptLoadPromise = null;
}

/**
 * Loads the contextual-comments script and tracks its readiness.
 *
 * @param shouldLoad Defaults to `true` for callers that only ever mount while the feature is already on
 * (e.g. `ContextualCommentsPanelContent`, gated by its parent's JSX) — they don't need to pass anything.
 * Pass `false` explicitly from call sites that run unconditionally regardless of the feature toggle
 * (e.g. `useDealCommentsPanel`, called on every render since hooks can't be conditional) — otherwise
 * the script loads even when the feature is off.
 */
export function useContextualComments(shouldLoad = true): { ready: boolean; error: boolean } {
    const [ready, setReady] = useState(() => !!customElements.get(ELEMENT_NAME));
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!shouldLoad || ready) return;
        const start = performance.now();
        let failed = false;
        loadContextualCommentsScript()
            .then(() => {
                setReady(true);
            })
            .catch(() => {
                failed = true;
                setError(true);
            })
            .finally(() => {
                if (typeof newrelic === 'object') {
                    if (failed) {
                        newrelic.addPageAction(DEAL_COMMENTS_NR_ACTIONS.scriptFailed, {
                            durationMs: Math.round(performance.now() - start)
                        });
                    } else {
                        newrelic.addPageAction(DEAL_COMMENTS_NR_ACTIONS.scriptLoaded, {
                            durationMs: Math.round(performance.now() - start)
                        });
                    }
                }
            });
    }, [shouldLoad]);

    return { ready, error };
}
